import styles from "./styles.module.scss";
import { DisplayText, Button } from "@shopify/polaris";
import cn from "classnames";
import useTranslation from "next-translate/useTranslation";
import Cards from "./Cards";
import { useCallback, useEffect, useMemo, useState } from "react";
import { signUtmParams } from "../../../../helpers/signUtmParams";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { checkHashTag } from "../../../../helpers/checkHashTag";
import PricingTable from "./PricingTable";
import RfOrderCulc from "./ RfOrderCulc";

export const MONTHLY = "monthly";
export const YEARLY = "yearly";
export const MONTHLY_HASH_TAG = "pricing-monthly";
export const YEARLY_HASH_TAG = "pricing-yearly";

const PricingTemplate = ({
  namespace,
  link,
  plans,
  rcCulc,
  linkDisabled = false,
}) => {
  const { t, lang } = useTranslation(namespace);

  const [currentPayment, setCurrentPayment] = useState(MONTHLY);
  const router = useRouter();
  const handleClick = useCallback(
    (payment, hashTag) => () => {
      setCurrentPayment(payment);
      router.push(router.pathname + "#" + hashTag, undefined, {
        shallow: true,
      });
    },
    [currentPayment]
  );

  const monthly = useMemo(() => {
    return plans.monthly;
  }, [plans.monthly]);
  const yearly = useMemo(() => plans.yearly, [plans]);

  const pricing = useMemo(
    () => ({
      header: t("pricing.header"),
      subheader: t("pricing.subheader"),
      link: {
        text: t("pricing.link"),
        href: signUtmParams(link, router),
      },
      payment: {
        yearly: {
          name: t("pricing.payment.yearly.name"),
        },
        monthly: {
          name: t("pricing.payment.monthly.name"),
          free: {
            price: t("pricing.payment.monthly.free.priceValue"),
            label: t("pricing.payment.monthly.label"),
          },
          starter: {
            price: t("pricing.payment.monthly.starter.priceValue"),
            label: t("pricing.payment.monthly.label"),
          },
          payAsGo: {
            price: t("pricing.payment.monthly.payAsGo.priceValue"),
            label: t("pricing.payment.monthly.label"),
          },
          standard: {
            price: t("pricing.payment.monthly.standard.priceValue"),
            label: t("pricing.payment.monthly.label"),
          },
          advance: {
            price: t("pricing.payment.monthly.advance.priceValue"),
            label: t("pricing.payment.monthly.label"),
          },
        },
      },
    }),
    [t, link, router]
  );
  const pricingContent = useMemo(
    () => (
      <>
        {currentPayment === MONTHLY && (
          <PricingTable
            namespace={namespace}
            plan={monthly}
            link={link}
            pricing={pricing}
            linkDisabled={linkDisabled}
          />
        )}
        {currentPayment === YEARLY && yearly && (
          <PricingTable
            namespace={namespace}
            plan={yearly}
            link={link}
            pricing={pricing}
            linkDisabled={linkDisabled}
          />
        )}
      </>
    ),
    [currentPayment, plans]
  );

  useEffect(() => {
    if (checkHashTag(router, YEARLY_HASH_TAG)) {
      setCurrentPayment(YEARLY);
      router.push(router.pathname + "#pricing-yearly", undefined, {
        shallow: true,
      });
    }
  }, []);

  return (
    <section className={cn(styles["pricing"], "pricing", `lang-${lang}`)}>
      <header
        className={cn(
          styles["pricing__header-wrapper"],
          "pricing__header-wrapper"
        )}
      >
        <DisplayText size="extraLarge" element="h3">
          {pricing.header}
        </DisplayText>
        <div className={styles["culc-wrapper"]}>
          {rcCulc && (
            <div className={styles["culc"]}>
              <RfOrderCulc />
            </div>
          )}
          <div
            className={cn(styles["header-text-wrapper"], {
              [styles["header-text-wrapper--culc"]]: rcCulc,
            })}
          >
            <p className={styles["pricing__subheader"]}>{pricing.subheader}</p>
            <div className={styles["pricing__link-wrapper"]}>
              <Button
                url={pricing.link.href}
                external
                primary
                disabled={linkDisabled}
              >
                {pricing.link.text}
              </Button>
            </div>
          </div>
        </div>
      </header>
      {yearly && (
        <div className={styles["pricing__switcher-wrapper"]}>
          <button
            data-dome="some"
            onClick={handleClick(MONTHLY, MONTHLY_HASH_TAG)}
            className={cn(styles["pricing__switcher"], {
              [styles["pricing__switcher--active"]]: currentPayment !== MONTHLY,
            })}
          >
            {pricing.payment.monthly.name}
          </button>
          <button
            onClick={handleClick(YEARLY, YEARLY_HASH_TAG)}
            className={cn(styles["pricing__switcher"], {
              [styles["pricing__switcher--active"]]: currentPayment !== YEARLY,
            })}
          >
            {pricing.payment.yearly.name}
          </button>
        </div>
      )}
      {pricingContent}
      <Cards
        namespace={namespace}
        payment={currentPayment}
        plans={plans}
        link={link}
      />
    </section>
  );
};

PricingTemplate.propTypes = {
  namespace: PropTypes.oneOf(["selectors", "reactFlow", "recharge"]),
};

export default PricingTemplate;
